import { CdkTrapFocus } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  Output,
} from "@angular/core";

import {
  claimEscape,
  decideEscape,
  isDismissEscape,
} from "@bitwarden/web-vault/app/shared/utils/escape-claim";

/** Mounted overlays, most recent last - the one on top takes an Escape aimed at nothing. */
const openOverlays: HTMLElement[] = [];

/**
 * Modal behaviour for a hand-rolled full-screen / slide-over overlay (an `@if`-rendered
 * `tw-fixed tw-inset-0` panel rather than a MatDialog), matching what MatDialog gives for free:
 * - exposed as a modal dialog (role="dialog", aria-modal),
 * - keyboard focus moves into it on open, stays trapped inside it, and returns to whatever had
 *   focus before (usually the trigger) when it is destroyed (CdkTrapFocus with auto-capture),
 * - Escape emits `modalOverlayDismiss` so the host can close it - unless something inside used
 *   that Escape first (preventDefault() / claimEscape(), e.g. releasing a grabbed row, or a
 *   dropdown that collapsed; see decideEscape).
 *
 * Escape is caught in the document's capture phase, so it also works after focus fell to <body>
 * (a click on a non-focusable part of the overlay), and the decision is queued ahead of any
 * dialog the overlay sits in - whose DialogEscapeService then sees this overlay's claim and stays
 * open.
 */
@Directive({
  selector: "[appModalOverlay]",
  standalone: true,
  hostDirectives: [CdkTrapFocus],
  host: {
    role: "dialog",
    "aria-modal": "true",
    "[attr.aria-label]": "ariaLabel",
  },
})
export class ModalOverlayDirective {
  /** Accessible name, when the overlay has no heading wired up with aria-labelledby. */
  @Input("appModalOverlayLabel") ariaLabel: string | null = null;
  @Output() modalOverlayDismiss = new EventEmitter<void>();

  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  constructor() {
    // Read in CdkTrapFocus.ngAfterContentInit, which runs after this constructor.
    inject(CdkTrapFocus, { self: true }).autoCapture = true;

    openOverlays.push(this.host);
    const onKeydown = (event: KeyboardEvent) => this.onDocumentKeydown(event);
    // Outside Angular: only an actual dismissal needs change detection.
    this.zone.runOutsideAngular(() => this.document.addEventListener("keydown", onKeydown, true));
    inject(DestroyRef).onDestroy(() => {
      this.document.removeEventListener("keydown", onKeydown, true);
      openOverlays.splice(openOverlays.indexOf(this.host), 1);
    });
  }

  private onDocumentKeydown(event: KeyboardEvent): void {
    if (!isDismissEscape(event) || !this.ownsTarget(event)) {
      return;
    }
    decideEscape(this.host, event, (claimedInside) => {
      if (claimedInside || !this.host.isConnected) {
        return;
      }
      claimEscape(event);
      this.zone.run(() => this.modalOverlayDismiss.emit());
    });
  }

  /**
   * Focus is in this overlay (not in a modal nested inside it), or nowhere in particular and this
   * is the overlay on top.
   */
  private ownsTarget(event: KeyboardEvent): boolean {
    const target = event.target;
    if (target === this.document.body || target === this.document.documentElement) {
      return openOverlays[openOverlays.length - 1] === this.host;
    }
    return target instanceof Element && target.closest("[aria-modal='true']") === this.host;
  }
}
